// login component
import React, { FormEvent, useState } from "react";
import AuthLayout from "../../app-layouts/AuthLayout";
import apiClient from "../../api-services/ApiClient";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  RegisterStepTwoSchema,
  RegisterStepTwoType,
} from "../../app-schemas/auth.schema";
import { inputStyle } from "../../app-util/helpers";
import { Checkbox } from "../../app-components/CheckboxComponent";
import { successAlert } from "../../app-util/SweetAlert";
import { Link, useNavigate } from "react-router-dom";
import { SITELINKS } from "../../app-routes/links";
import { storeUser } from "../../api-services/process";
import { HoneConnectLogo } from "../../assets/img";
import { CiPower } from "react-icons/ci";

const RegisterStepTwo = () => {
  sessionStorage.removeItem("linkedinCallbackCalled");
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [serverError, setServerError] = useState<string>("");
  const [registrationData, setRegistrationData] = useState<
    Partial<RegisterStepTwoType>
  >({});
  const [errors, setErrors] = useState<
    Partial<Record<keyof RegisterStepTwoType, string>>
  >({});

  // handle-input-change
  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | any
    >,
  ) => {
    const { name, value } = e.target;
    setRegistrationData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  // handle-phone-change
  const handlePhoneChange = (value: string) => {
    setRegistrationData((prevState) => ({
      ...prevState,
      phone_number: value,
    }));
  };

  // handle-DOB-change
  const handleDOB = (date: any) => {
    const formattedDate = date ? date.toISOString().split("T")[0] : ""; // Format as YYYY-MM-DD
    setRegistrationData((prevState) => ({
      ...prevState,
      DOB: formattedDate,
    }));
  };

  // handle-checkbox-change
  const handleCheckBox = (name: string, status?: boolean) => {
    setRegistrationData((prevState) => ({
      ...prevState,
      [name]: status,
    }));
  };

  // Calculate the date for 18 years ago
  const today = new Date();
  const eighteenYearsAgo = new Date(
    today.getFullYear() - 18,
    today.getMonth(),
    today.getDate(),
  );

  // handle-login-submit
  const handleSignUpStepTwo = async (e: FormEvent) => {
    e.preventDefault();
    setErrors({});
    setServerError("");

    const result = RegisterStepTwoSchema.safeParse(registrationData);
    if (result.success) {
      try {
        setLoading(true);
        const res = await apiClient.post("register-step-two", result.data);
        successAlert();
        if (res.data?.user) storeUser(res.data?.user);

        navigate(SITELINKS.REGISTER_STEP_THREE);
      } catch (error: any) {
        console.log(error);
        setServerError(JSON.stringify(error));
      } finally {
        setLoading(false);
      }
    } else {
      const formattedErrors: Partial<
        Record<keyof RegisterStepTwoType, string>
      > = {};

      result.error.errors.forEach((err) => {
        if (err.path.length > 0) {
          const key = err.path[0] as keyof RegisterStepTwoType;
          formattedErrors[key] = err.message;
        }
      });

      setErrors(formattedErrors);
      console.log(formattedErrors);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await apiClient.post("logout");
      localStorage.clear();
      navigate(SITELINKS.LOGIN);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <div className="lg:col-span-3 bg-white shadow relative h-[100vh]">
        {/* stepper area section */}
        <div className="bg-white w-full mt-5 pl-5">
          <button
            type="button"
            onClick={() => {
              logout();
            }}
            className="fixed gap-4 p-2 hover:bg-gray-700 dark:hover:bg-slate-800 bg-red-700 text-white shadow rounded-full font-[600]"
          >
            <CiPower className="text-2xl" />
          </button>

          <div className="w-1/2 mx-auto pt-8 flex gap-2 mb-10 justify-center">
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-honeConnectPurple h-[5px] w-[40px] shadow rounded-full"></div>
            <div className="bg-gray-300 h-[5px] w-[40px] shadow rounded-full"></div>
          </div>

          <div className="text-sm font-semibold flex justify-center px-4">
            <Link to="#!" className="text-honeConnectPurple flex">
              <img
                src={HoneConnectLogo}
                alt="HoneConnect"
                className="h-[35px]"
              />
            </Link>

            <p className="uppercase text-honeConnectGray hidden sm:block">
              Step 2/3
            </p>
          </div>
          {/* <!-- page lead text --> */}
          <div className="px-5 pt-6 pb-5">
            <h4 className="text-[2.5rem] text-honeConnectBlack font-medium mb-2.5 text-honeConnectPurple leading-[35px] text-center">
              Enter your <br />
              details
            </h4>
            <p className="text-sm mb-4 font-medium w-4/5 hidden">
              Don't have an account?, it takes less than a minute.
            </p>
          </div>
        </div>

        {/* form section */}
        <div className="relative top-[-10px] w-full pt-4 lg:pt-20 pb-0 px-5 lg:px-10 lg:px-28 text-honeConnectPurple bg-white rounded-t-lg">
          <form
            onSubmit={handleSignUpStepTwo}
            autoComplete="off"
            autoCorrect="off"
            className="space-y-2 grid gap-2 text-gray-500"
          >
            {/* User ID */}
            <div className="flex flex-col w-full">
              <label htmlFor="userID" className="mb-0.5 text-[12px]">
                Username
              </label>
              <input
                value={registrationData?.username
                  ?.replace(/\s+/g, "")
                  .toLocaleLowerCase()}
                onChange={handleInputChange}
                type="text"
                name="username"
                placeholder="Enter a username"
                className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
              />
              <span className="text-red-600 text-xs">{errors.username}</span>
            </div>

            <div className="flex items-center gap-2">
              <div className="flex flex-col w-1/2">
                <label htmlFor="dob" className="mb-0.5 text-[12px]">
                  Date of Birth
                </label>

                <DatePicker
                  id="dob"
                  name="DOB"
                  selected={
                    registrationData.DOB
                      ? new Date(registrationData.DOB)
                      : eighteenYearsAgo
                  }
                  onChange={(date) => handleDOB(date)}
                  dateFormat="yyyy-MM-dd"
                  maxDate={eighteenYearsAgo}
                  showYearDropdown
                  showMonthDropdown
                  dropdownMode="select"
                  placeholderText="Select your date of birth"
                  className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                />

                <span className="text-red-600 text-xs">{errors.DOB}</span>
              </div>

              <div className="flex flex-col w-1/2">
                <label htmlFor="gender" className="mb-0.5 text-[12px]">
                  Gender
                </label>
                <select
                  value={registrationData.gender}
                  name="gender"
                  onChange={handleInputChange}
                  className={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                >
                  <option value="" className="bg-white text-xs">
                    Select Gender
                  </option>
                  <option value="MALE" className="bg-white text-xs">
                    Male
                  </option>
                  <option value="FEMALE" className="bg-white text-xs">
                    Female
                  </option>
                  <option value="OTHERS" className="bg-white text-xs">
                    Not Specified
                  </option>
                </select>
                <span className="text-red-600 text-xs">{errors.gender}</span>
              </div>
            </div>

            {/* Phone Number */}
            <div className="flex flex-col w-full" id="phone_area">
              <label htmlFor="phone" className="mb-0.5 text-[12px]">
                Phone Number
              </label>
              <PhoneInput
                country="ng"
                value={registrationData.phone_number as string}
                onChange={handlePhoneChange}
                inputClass={`${inputStyle} bg-transparent border-gray-200 text- rounded-[0px] outline-none focus:outline-none font-medium text-[12px] shadow-sm hover:shadow`}
                inputProps={{ autoComplete: "off" }}
              />
              <span className="text-red-600 text-xs">
                {errors.phone_number}
              </span>
            </div>

            {/* Terms and Conditions */}
            <div className="flex items-center gap-2">
              <Checkbox
                checked={registrationData.isEligible}
                onChange={() =>
                  handleCheckBox("isEligible", !registrationData.isEligible)
                }
              />
              <p className="text-xs text-gray-500">I am 18 years or older</p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={registrationData.isAgreedTerms}
                onChange={() =>
                  handleCheckBox(
                    "isAgreedTerms",
                    !registrationData.isAgreedTerms,
                  )
                }
              />
              <p className="text-xs text-gray-500">
                I accept HoneConnect's{" "}
                <Link
                  to={"/terms-and-conditions"}
                  className="text-honeConnectOrange"
                >
                  Terms and Conditions
                </Link>
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                checked={registrationData.isAgreedPrivacy}
                onChange={() =>
                  handleCheckBox(
                    "isAgreedPrivacy",
                    !registrationData.isAgreedPrivacy,
                  )
                }
              />
              <p className="text-xs  text-gray-500">
                I accept HoneConnect's{" "}
                <Link to={"/privacy-policy"} className="text-honeConnectOrange">
                  Privacy Policy.
                </Link>
              </p>
            </div>

            <span className="text-red-600 text-xs">
              {errors.isEligible ||
                errors.isAgreedTerms ||
                errors.isAgreedPrivacy}
            </span>

            {/* Error Message */}
            {serverError && (
              <p className="text-red-600 text-center text-xs">{serverError}</p>
            )}

            {/* Submit Button */}
            <div className="pb-5">
              <button
                type="submit"
                className="disabled:bg-gray-600 bg-honeConnectPurple text-white text-sm w-full p-3 py-4 border-0 mt-2 hover:shadow-2xl rounded-lg"
                disabled={loading}
              >
                {loading ? "Loading..." : "Continue"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </AuthLayout>
  );
};

export default RegisterStepTwo;
